import { NUMBER_FORMAT_LOCALE } from 'shared-domains/utilities/number-format.helper';
import { registerLocaleData } from '@angular/common';
import enGb from '@angular/common/locales/en-GB';
import { ApplicationConfig, LOCALE_ID, importProvidersFrom, inject, isDevMode, provideAppInitializer } from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, RouteReuseStrategy, withInMemoryScrolling } from "@angular/router";
import { WorkspaceRecordReuseStrategy } from './core/services/workspace/workspace-record-reuse.strategy';
import { TranslocoService, provideTransloco } from "@jsverse/transloco";
import { appRoutes } from "app/app.routes";
import { provideAuth } from "shared-service/services/auth/auth.provider";
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { forkJoin, map, switchMap, tap } from "rxjs";
import { AppStorageService } from "shared-service/app-storage/app.storage";
import { ConfigService } from "shared-service/config/config.service";
import { TranslocoHttpLoader } from "shared-service/transloco/transloco.http-loader";
import { LoggerService } from "shared-service/services/logger/logger.service";
import { SystemInfoService } from "./core/services/api/system-management/information/system-info.service";
import { TenantBrandingService } from "./core/services/branding/tenant-branding.service";

registerLocaleData(enGb);

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: LOCALE_ID, useValue: NUMBER_FORMAT_LOCALE },
        provideAnimations(),
        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: "enabled" })),
        { provide: RouteReuseStrategy, useClass: WorkspaceRecordReuseStrategy },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: "en",
                        label: "English",
                    },
                    {
                        id: "vi",
                        label: "Vietnamese",
                    },
                    {
                        id: "de",
                        label: "Deutsch",
                    },
                ],
                defaultLang: "en",
                fallbackLang: "en",
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            // Config
            const configService = inject(ConfigService);
            // Tải cấu hình API
            const loadConfig$ = configService.loadConfigs();

            // Transloco
            const translocoService = inject(TranslocoService);
            const appStorageService = inject(AppStorageService);
            const availableLangs = translocoService.getAvailableLangs();
            const availableLangIds = Array.isArray(availableLangs) ? availableLangs.map((lang: any) => (typeof lang === "string" ? lang : lang?.id)).filter((langId): langId is string => !!langId) : [];
            const defaultLang = translocoService.getDefaultLang();
            const preferredLang = appStorageService.uiLanguage;
            const startupLang = availableLangIds.includes(preferredLang) ? preferredLang : availableLangIds.includes(defaultLang) ? defaultLang : (availableLangIds[0] ?? defaultLang);

            translocoService.setActiveLang(startupLang);
            appStorageService.uiLanguage = startupLang;
            // Tải ngôn ngữ mặc định
            const loadLang$ = translocoService.load(startupLang);

            // SystemInfoService — inject sau khi cần
            const systemInfoService = inject(SystemInfoService);

            // Thương hiệu theo tên miền (ẩn danh). Chạy SAU loadConfig$ vì nó cần baseUrl đã nạp,
            // và cố ý không nằm trong forkJoin chặn: hỏng nhánh này chỉ mất logo riêng, không được
            // phép giữ app ở màn splash.
            const brandingService = inject(TenantBrandingService);

            // Đảm bảo cả hai quá trình đều hoàn thành, sau đó gọi Keycloak setting
            return forkJoin([loadLang$, loadConfig$]).pipe(
                tap(() => brandingService.loadForHost().subscribe()),
                switchMap(() => systemInfoService.getKeycloak()),
                map(() => undefined),
            );
        }),

        // Auth (framework/Material-independent; icons via KeenIcons font in vlp-icon)
        provideAuth(),

        // Logger
        importProvidersFrom(
            LoggerModule.forRoot({
                // Default level, will be overridden after loading config
                level: NgxLoggerLevel.DEBUG, // Mức độ log trong ứng dụng
                serverLogLevel: NgxLoggerLevel.ERROR, // Mức độ log gửi lên server
                disableConsoleLogging: false, // Nếu muốn log ra console
                enableSourceMaps: true, // Bật tính năng source maps
                proxiedSteps: 1, // Bỏ qua 1 bước trong stack trace để lấy đúng vị trí gọi
            }),
        ),
        LoggerService,
    ],
};
